import {
  OrderEligibilityInformation,
  OrderRefundOptionsItem,
  Traveler,
} from '@dapi/sdk';
import {
  getOrderCancelAndRefundEligibilityStatus,
} from '@dapi/sdk/helpers';
import {
  OrderModel,
} from '@des/store/order';

/**
 * Returns whether the order is already cancelled or not
 *
 * @param order
 * @returns boolean
 */
function isOrderAlreadyCancelled(order: OrderModel): boolean {
  return !order.air;
}

/**
 * Returns whether every original refund option has payment methods or not
 *
 * @param refundOptions refundOptions to check
 * @returns boolean
 */
function doesEveryOriginalRefundOptionHasPaymentMethods(refundOptions: OrderRefundOptionsItem[]): boolean {
  return refundOptions.every((option) => {
    const defaultOptionPaymentDetailsList = option.refundMethodOptions
      ?.filter((refundMethodOption) => refundMethodOption.id.endsWith('DEFAULT'))
      ?.map((refundMethodOption) => refundMethodOption.paymentDetails);
    return !!defaultOptionPaymentDetailsList?.every(
      (defaultOptionPaymentDetails) => defaultOptionPaymentDetails?.length && defaultOptionPaymentDetails.every((defaultOptionPaymentDetail) => defaultOptionPaymentDetail.paymentMethod)
    );
  });
}

/**
 * Returns whether the order is eligible to refund or not
 *
 * @param order
 * @param orderEligibilities
 * @returns boolean
 */
export function isEligibleToRefund(order: OrderModel, orderEligibilities: OrderEligibilityInformation): boolean {
  if (!isOrderAlreadyCancelled(order)) {
    const eligibilityStatus = orderEligibilities && getOrderCancelAndRefundEligibilityStatus(orderEligibilities);
    if (eligibilityStatus === 'manuallyRefundable') {
      return true;
    } else if (eligibilityStatus === 'refundable') {
      const refundOptions = orderEligibilities.cancelAndRefund?.refundOptions?.filter((option) => option.status !== 'notRefundable');
      const infantTravelersIds = order.travelers?.filter((traveler) => traveler.passengerTypeCode === 'INF').map((traveler) => traveler.id);
      // Filter infant travelers refund options since they don't have a refund method
      const refundOptionsWithoutInfants = refundOptions?.filter((option) => !option.travelerIds?.every((id) => infantTravelersIds?.includes(id)));
      return !!refundOptionsWithoutInfants && doesEveryOriginalRefundOptionHasPaymentMethods(refundOptionsWithoutInfants);
    }
  }
  return false;
}

/**
 * Returns whether all the passengers have been selected for refund or not
 *
 * @param selectedPassengerIds
 * @param travelers
 * @returns boolean
 */
export function areAllPassengersSelectedForRefund(selectedPassengerIds: string[], travelers: Traveler[]): boolean {
  // Infant can't be selected for refund separately. They are always selected with an Adult
  const travlersListWithoutInfant = travelers?.filter((traveler) => traveler.passengerTypeCode !== 'INF');
  return selectedPassengerIds.length === travlersListWithoutInfant?.length;
}
